// Test Content Generator for PostSub.io Reader Experience
// This file contains realistic test data for reviewing the reader experience

export interface TestArticle {
  id: string;
  title: string;
  content: string;
  author: string;
  authorId: string;
  newsletter: string;
  newsletterId: string;
  publishedAt: Date;
  readTime: number;
  tags: string[];
  imageUrl?: string;
  likes: number;
  isLiked: boolean;
  isBookmarked: boolean;
  isStarred: boolean;
  isFlagged: boolean;
  priority: 'urgent' | 'normal' | 'low';
  category: string;
  excerpt: string;
  views: number;
  shares: number;
  comments: number;
}

export interface TestNewsletter {
  id: string;
  title: string;
  author: string;
  description: string;
  category: string;
  subscriberCount: number;
  avgRating: number;
  postCount: number;
  frequency: string;
  lastPost: Date;
  tags: string[];
  avatar?: string;
  color: string;
  isRecommended: boolean;
  isTrending: boolean;
  isNew: boolean;
  isSubscribed: boolean;
  engagementScore: number;
  qualityScore: number;
  readingTime: number;
  language: string;
  price: 'free' | 'paid';
  priceAmount?: number;
}

export const testArticles: TestArticle[] = [
  {
    id: '1',
    title: 'The Future of Web Development: What Every Developer Needs to Know',
    content: `
      <h2>Introduction</h2>
      <p>The web development landscape is evolving at an unprecedented pace. As we move into 2024, developers are facing new challenges and opportunities that will fundamentally reshape how we build for the web.</p>
      
      <p>In this comprehensive guide, we'll explore the key trends, technologies, and practices that are shaping the future of web development. From React Server Components to WebAssembly, from AI-powered development tools to the rise of edge computing – we'll cover it all.</p>
      
      <h2>The Rise of React Server Components</h2>
      <p>React Server Components represent one of the most significant shifts in how we think about React applications. Unlike traditional client-side components, RSCs run on the server, enabling developers to build more performant applications with better SEO and faster initial page loads.</p>
      
      <p>The key benefits include:</p>
      <ul>
        <li><strong>Reduced bundle size:</strong> Server components don't ship JavaScript to the client</li>
        <li><strong>Better performance:</strong> Faster initial page loads and improved Core Web Vitals</li>
        <li><strong>Enhanced SEO:</strong> Server-rendered content is immediately available to search engines</li>
        <li><strong>Direct database access:</strong> Server components can directly access databases and APIs</li>
      </ul>
      
      <h2>WebAssembly: Bringing Native Performance to the Web</h2>
      <p>WebAssembly (WASM) is revolutionizing web performance by allowing developers to run code written in languages like C, C++, and Rust directly in the browser. This opens up new possibilities for computationally intensive applications.</p>
      
      <p>Real-world applications of WebAssembly include:</p>
      <ul>
        <li>Image and video processing applications</li>
        <li>Games and interactive 3D experiences</li>
        <li>Scientific computing and data analysis</li>
        <li>Cryptocurrency mining and blockchain applications</li>
      </ul>
      
      <h2>AI-Powered Development Tools</h2>
      <p>The integration of artificial intelligence into development workflows is accelerating rapidly. Tools like GitHub Copilot, ChatGPT, and specialized coding assistants are changing how developers write, debug, and optimize code.</p>
      
      <p>Key areas where AI is making an impact:</p>
      <ul>
        <li><strong>Code generation:</strong> Automated code completion and generation</li>
        <li><strong>Bug detection:</strong> AI-powered static analysis and error detection</li>
        <li><strong>Performance optimization:</strong> Intelligent code optimization suggestions</li>
        <li><strong>Documentation:</strong> Automated documentation generation and maintenance</li>
      </ul>
      
      <h2>The Edge Computing Revolution</h2>
      <p>Edge computing is bringing computation and data storage closer to the sources of data, reducing latency and improving user experience. For web developers, this means new opportunities to build faster, more responsive applications.</p>
      
      <p>Edge computing benefits for web development:</p>
      <ul>
        <li>Reduced latency for global users</li>
        <li>Better performance for real-time applications</li>
        <li>Improved reliability and fault tolerance</li>
        <li>Cost-effective scaling for high-traffic applications</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>The future of web development is bright and full of exciting possibilities. As developers, we need to stay curious, keep learning, and embrace the changes that are coming our way. The technologies and practices we've discussed today are just the beginning of what's possible.</p>
      
      <p>What trends are you most excited about? Let me know in the comments below!</p>
    `,
    author: 'Sarah Chen',
    authorId: 'sarah-chen',
    newsletter: 'Tech Weekly',
    newsletterId: 'tech-weekly',
    publishedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
    readTime: 12,
    tags: ['web development', 'react', 'webassembly', 'ai', 'edge computing'],
    imageUrl: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&h=400&fit=crop',
    likes: 127,
    isLiked: false,
    isBookmarked: true,
    isStarred: false,
    isFlagged: false,
    priority: 'urgent',
    category: 'Technology',
    excerpt: 'Exploring the key trends, technologies, and practices that are shaping the future of web development in 2024.',
    views: 2847,
    shares: 89,
    comments: 23
  },
  {
    id: '2',
    title: 'Understanding React Server Components: A Deep Dive',
    content: `
      <h2>What Are React Server Components?</h2>
      <p>React Server Components (RSCs) are a new way to build React applications that allows components to render on the server. This is different from Server-Side Rendering (SSR) because RSCs can be mixed with client components in the same component tree.</p>
      
      <p>Think of RSCs as a hybrid approach that combines the best of both server-side and client-side rendering. You can have some components that run on the server (for data fetching and heavy computations) and others that run on the client (for interactivity).</p>
      
      <h2>Key Benefits of Server Components</h2>
      <p>Server Components offer several advantages over traditional client-side components:</p>
      
      <h3>1. Reduced JavaScript Bundle Size</h3>
      <p>Since Server Components run on the server, their code doesn't need to be sent to the client. This means smaller JavaScript bundles and faster page loads.</p>
      
      <h3>2. Direct Database Access</h3>
      <p>Server Components can directly access databases, file systems, and other server-side resources without needing API routes. This simplifies data fetching and reduces the number of round trips.</p>
      
      <h3>3. Better SEO</h3>
      <p>Content rendered by Server Components is available immediately when the page loads, making it perfect for SEO-critical content.</p>
      
      <h2>How to Use Server Components</h2>
      <p>Using Server Components is straightforward. Simply create a component file and it will be a Server Component by default. Here's a simple example:</p>
      
      <pre><code>// app/posts/page.js
import { db } from './lib/db'

export default async function PostsPage() {
  const posts = await db.posts.findMany()
  
  return (
    <div>
      <h1>All Posts</h1>
      {posts.map(post => (
        <div key={post.id}>
          <h2>{post.title}</h2>
          <p>{post.content}</p>
        </div>
      ))}
    </div>
  )
}</code></pre>
      
      <h2>Mixing Server and Client Components</h2>
      <p>One of the most powerful features of RSCs is the ability to mix server and client components in the same tree. You can have a Server Component that fetches data and passes it to a Client Component for interactivity.</p>
      
      <pre><code>// Server Component
import { db } from './lib/db'
import InteractiveButton from './InteractiveButton'

export default async function PostCard({ postId }) {
  const post = await db.posts.findById(postId)
  
  return (
    <div>
      <h2>{post.title}</h2>
      <p>{post.content}</p>
      <InteractiveButton postId={postId} />
    </div>
  )
}</code></pre>
      
      <h2>Best Practices</h2>
      <p>When working with Server Components, keep these best practices in mind:</p>
      
      <ul>
        <li>Use Server Components for data fetching and heavy computations</li>
        <li>Use Client Components for interactivity and browser APIs</li>
        <li>Keep Server Components pure and avoid side effects</li>
        <li>Pass serializable props between Server and Client Components</li>
        <li>Use the 'use client' directive only when necessary</li>
      </ul>
      
      <h2>Common Pitfalls</h2>
      <p>There are some common mistakes to avoid when working with Server Components:</p>
      
      <ul>
        <li>Don't use browser-only APIs in Server Components</li>
        <li>Avoid passing functions or non-serializable data as props</li>
        <li>Don't use hooks in Server Components</li>
        <li>Be careful with async/await in component functions</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>React Server Components represent a significant evolution in how we build React applications. By understanding when and how to use them, you can create more performant and maintainable applications.</p>
      
      <p>Start experimenting with Server Components in your next project and see how they can improve your development experience!</p>
    `,
    author: 'Mike Johnson',
    authorId: 'mike-johnson',
    newsletter: 'React Digest',
    newsletterId: 'react-digest',
    publishedAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
    readTime: 15,
    tags: ['react', 'server-components', 'nextjs', 'performance', 'javascript'],
    imageUrl: 'https://images.unsplash.com/photo-1633356122544-f134324a6cce?w=800&h=400&fit=crop',
    likes: 89,
    isLiked: true,
    isBookmarked: false,
    isStarred: true,
    isFlagged: false,
    priority: 'normal',
    category: 'Technology',
    excerpt: 'A comprehensive guide to React Server Components, covering benefits, usage patterns, and best practices.',
    views: 1923,
    shares: 45,
    comments: 17
  },
  {
    id: '3',
    title: 'Design Systems That Scale: Lessons from Building at Scale',
    content: `
      <h2>The Challenge of Scaling Design</h2>
      <p>Building a design system that can scale with your product and team is one of the most challenging aspects of modern product development. As your team grows and your product evolves, maintaining consistency and efficiency becomes increasingly difficult.</p>
      
      <p>In this article, I'll share the lessons learned from building and maintaining design systems at companies like Airbnb, Stripe, and GitHub. We'll cover everything from component architecture to team collaboration strategies.</p>
      
      <h2>What Makes a Design System Successful?</h2>
      <p>A successful design system is more than just a collection of components. It's a living, breathing system that evolves with your product and serves the needs of your entire organization.</p>
      
      <h3>Key Characteristics:</h3>
      <ul>
        <li><strong>Consistency:</strong> All components follow the same design principles</li>
        <li><strong>Scalability:</strong> Easy to add new components and modify existing ones</li>
        <li><strong>Accessibility:</strong> Built with accessibility in mind from the ground up</li>
        <li><strong>Documentation:</strong> Comprehensive documentation and examples</li>
        <li><strong>Adoption:</strong> Easy for teams to adopt and use</li>
      </ul>
      
      <h2>Component Architecture Patterns</h2>
      <p>The way you structure your components can make or break your design system. Here are some proven patterns that work well at scale:</p>
      
      <h3>1. Atomic Design Methodology</h3>
      <p>Atomic Design breaks down components into five distinct levels:</p>
      <ul>
        <li><strong>Atoms:</strong> Basic building blocks (buttons, inputs, labels)</li>
        <li><strong>Molecules:</strong> Simple groups of atoms (search form, navigation item)</li>
        <li><strong>Organisms:</strong> Complex UI components (header, sidebar, product grid)</li>
        <li><strong>Templates:</strong> Page-level objects (homepage, product page)</li>
        <li><strong>Pages:</strong> Specific instances of templates</li>
      </ul>
      
      <h3>2. Compound Components</h3>
      <p>Compound components allow you to create flexible, composable APIs. Instead of one large component with many props, you create multiple related components that work together.</p>
      
      <pre><code>// Instead of this:
&lt;Select 
  options={options}
  value={value}
  onChange={onChange}
  placeholder="Choose an option"
  searchable
  multiSelect
/&gt;

// Use this:
&lt;Select&gt;
  &lt;Select.Trigger&gt;
    &lt;Select.Value placeholder="Choose an option" /&gt;
  &lt;Select.Trigger&gt;
  &lt;Select.Content&gt;
    &lt;Select.Search placeholder="Search options..." /&gt;
    {options.map(option => (
      &lt;Select.Item key={option.value} value={option.value}&gt;
        {option.label}
      &lt;Select.Item&gt;
    ))}
  &lt;Select.Content&gt;
&lt;Select&gt;</code></pre>
      
      <h2>Documentation Strategies</h2>
      <p>Great documentation is essential for design system adoption. Here's how to create documentation that actually gets used:</p>
      
      <h3>1. Living Documentation</h3>
      <p>Your documentation should be generated from your actual code, not written separately. This ensures it stays up-to-date and accurate.</p>
      
      <h3>2. Interactive Examples</h3>
      <p>Show, don't tell. Interactive examples let developers see components in action and experiment with different configurations.</p>
      
      <h3>3. Usage Guidelines</h3>
      <p>Provide clear guidelines on when and how to use each component. Include do's and don'ts with visual examples.</p>
      
      <h2>Team Collaboration</h2>
      <p>Design systems are built by teams, not individuals. Here's how to foster effective collaboration:</p>
      
      <h3>1. Cross-Functional Teams</h3>
      <p>Include designers, developers, and product managers in your design system team. Each brings unique perspectives and expertise.</p>
      
      <h3>2. Regular Reviews</h3>
      <p>Hold regular design reviews to ensure consistency and quality. Make it easy for team members to provide feedback.</p>
      
      <h3>3. Clear Ownership</h3>
      <p>Define clear ownership and responsibilities for different parts of the design system. This prevents confusion and ensures accountability.</p>
      
      <h2>Measuring Success</h2>
      <p>How do you know if your design system is successful? Here are some key metrics to track:</p>
      
      <ul>
        <li><strong>Adoption Rate:</strong> How many teams are using the design system?</li>
        <li><strong>Component Usage:</strong> Which components are most/least used?</li>
        <li><strong>Development Speed:</strong> How much faster are teams building new features?</li>
        <li><strong>Consistency Score:</strong> How consistent is the UI across different parts of the product?</li>
        <li><strong>Developer Satisfaction:</strong> How happy are developers with the design system?</li>
      </ul>
      
      <h2>Common Pitfalls to Avoid</h2>
      <p>Here are some common mistakes that can derail your design system:</p>
      
      <ul>
        <li><strong>Over-engineering:</strong> Don't try to solve every possible use case</li>
        <li><strong>Poor documentation:</strong> Inadequate documentation leads to low adoption</li>
        <li><strong>Lack of governance:</strong> Without proper governance, the system becomes inconsistent</li>
        <li><strong>Ignoring feedback:</strong> Not listening to users leads to poor adoption</li>
        <li><strong>Inflexibility:</strong> Being too rigid prevents innovation</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>Building a design system that scales is challenging but incredibly rewarding. By following these principles and learning from the experiences of others, you can create a system that serves your organization for years to come.</p>
      
      <p>Remember: a design system is never "done." It's a living, evolving system that grows with your product and team. Stay curious, keep learning, and don't be afraid to iterate.</p>
    `,
    author: 'Emma Wilson',
    authorId: 'emma-wilson',
    newsletter: 'Design Weekly',
    newsletterId: 'design-weekly',
    publishedAt: new Date(Date.now() - 6 * 60 * 60 * 1000),
    readTime: 18,
    tags: ['design systems', 'ui/ux', 'scalability', 'team collaboration', 'documentation'],
    imageUrl: 'https://images.unsplash.com/photo-1558655146-d09347e92766?w=800&h=400&fit=crop',
    likes: 156,
    isLiked: true,
    isBookmarked: true,
    isStarred: true,
    isFlagged: false,
    priority: 'normal',
    category: 'Design',
    excerpt: 'Learn how to build design systems that scale with your product and team, with lessons from companies like Airbnb and Stripe.',
    views: 3456,
    shares: 78,
    comments: 31
  },
  {
    id: '4',
    title: 'The Psychology of User Onboarding: How to Hook Users from Day One',
    content: `
      <h2>Why Onboarding Matters</h2>
      <p>User onboarding is make-or-break for your product. Studies show that 40-60% of users who sign up for a free trial will use your product once and never come back. The difference between success and failure often comes down to those first few minutes of user experience.</p>
      
      <p>In this article, we'll explore the psychology behind effective user onboarding and share practical strategies you can implement today.</p>
      
      <h2>The Psychology of First Impressions</h2>
      <p>Humans form first impressions in milliseconds, and these impressions are incredibly difficult to change. When users first encounter your product, they're making rapid judgments about:</p>
      
      <ul>
        <li>Whether it's worth their time</li>
        <li>How difficult it will be to learn</li>
        <li>Whether it will solve their problem</li>
        <li>If they can trust your product</li>
      </ul>
      
      <h2>The Hook Model: Creating Habit-Forming Products</h2>
      <p>Nir Eyal's Hook Model provides a framework for creating products that users can't put down. The model consists of four phases:</p>
      
      <h3>1. Trigger</h3>
      <p>Triggers are the cues that prompt users to take action. They can be external (notifications, emails) or internal (boredom, stress).</p>
      
      <h3>2. Action</h3>
      <p>The simplest behavior users can take in anticipation of a reward. The easier the action, the more likely users are to take it.</p>
      
      <h3>3. Variable Reward</h3>
      <p>Rewards that satisfy users' needs while leaving them wanting more. The variability keeps users engaged.</p>
      
      <h3>4. Investment</h3>
      <p>Users put something into the product (time, data, effort, money) that increases the likelihood of them returning.</p>
      
      <h2>Onboarding Best Practices</h2>
      <p>Here are proven strategies for creating effective onboarding experiences:</p>
      
      <h3>1. Start with Value, Not Features</h3>
      <p>Don't show users every feature your product has. Instead, focus on the core value proposition and help them achieve their first success quickly.</p>
      
      <h3>2. Use Progressive Disclosure</h3>
      <p>Reveal information gradually as users need it. Don't overwhelm them with everything at once.</p>
      
      <h3>3. Make It Interactive</h3>
      <p>Let users actually use your product during onboarding. Don't just show them screenshots or videos.</p>
      
      <h3>4. Provide Immediate Feedback</h3>
      <p>Give users clear feedback on their actions. Celebrate their successes and guide them when they make mistakes.</p>
      
      <h2>Common Onboarding Mistakes</h2>
      <p>Here are the most common mistakes that kill user engagement:</p>
      
      <ul>
        <li><strong>Too many steps:</strong> Keep onboarding short and focused</li>
        <li><strong>Generic messaging:</strong> Personalize the experience for each user</li>
        <li><strong>No clear next steps:</strong> Always tell users what to do next</li>
        <li><strong>Ignoring mobile:</strong> Many users will first experience your product on mobile</li>
        <li><strong>No progress indication:</strong> Users need to know how much is left</li>
      </ul>
      
      <h2>Measuring Onboarding Success</h2>
      <p>How do you know if your onboarding is working? Here are key metrics to track:</p>
      
      <ul>
        <li><strong>Completion Rate:</strong> What percentage of users complete onboarding?</li>
        <li><strong>Time to First Value:</strong> How long does it take users to see value?</li>
        <li><strong>Feature Adoption:</strong> Which features do users try first?</li>
        <li><strong>Retention Rate:</strong> Do users come back after onboarding?</li>
        <li><strong>User Feedback:</strong> What do users say about the experience?</li>
      </ul>
      
      <h2>Case Studies</h2>
      <p>Let's look at some examples of companies that have mastered user onboarding:</p>
      
      <h3>Slack: The Conversational Approach</h3>
      <p>Slack's onboarding feels like a conversation. They guide users through setup with friendly, helpful messages that feel personal and engaging.</p>
      
      <h3>Duolingo: Gamification Done Right</h3>
      <p>Duolingo turns language learning into a game. Users earn points, unlock achievements, and maintain streaks, making learning addictive.</p>
      
      <h3>Notion: The Blank Canvas</h3>
      <p>Notion starts users with a blank page and lets them explore. They provide templates and examples, but don't force a specific workflow.</p>
      
      <h2>Conclusion</h2>
      <p>Great onboarding is about understanding your users' psychology and creating experiences that guide them to success. Focus on value, make it interactive, and always measure your results.</p>
      
      <p>Remember: onboarding isn't just about getting users to sign up – it's about getting them to succeed with your product.</p>
    `,
    author: 'Alex Rodriguez',
    authorId: 'alex-rodriguez',
    newsletter: 'Product Insights',
    newsletterId: 'product-insights',
    publishedAt: new Date(Date.now() - 8 * 60 * 60 * 1000),
    readTime: 10,
    tags: ['onboarding', 'user experience', 'psychology', 'product management', 'conversion'],
    imageUrl: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=400&fit=crop',
    likes: 203,
    isLiked: false,
    isBookmarked: false,
    isStarred: false,
    isFlagged: false,
    priority: 'low',
    category: 'Product',
    excerpt: 'Learn the psychology behind effective user onboarding and discover strategies to hook users from day one.',
    views: 4567,
    shares: 123,
    comments: 45
  },
  {
    id: '5',
    title: 'Building Resilient Systems: Lessons from High-Scale Applications',
    content: `
      <h2>What is System Resilience?</h2>
      <p>System resilience is the ability of a system to continue operating in the face of failures, attacks, or unexpected load. In today's world of microservices, distributed systems, and cloud computing, building resilient systems is more important than ever.</p>
      
      <p>In this article, we'll explore the principles and patterns that help systems survive and thrive under pressure.</p>
      
      <h2>The Three Pillars of Resilience</h2>
      <p>Resilient systems are built on three fundamental pillars:</p>
      
      <h3>1. Fault Tolerance</h3>
      <p>Fault tolerance is the ability to continue operating when individual components fail. This is achieved through redundancy, graceful degradation, and proper error handling.</p>
      
      <h3>2. Scalability</h3>
      <p>Scalability is the ability to handle increased load by adding resources. This includes both horizontal scaling (adding more instances) and vertical scaling (adding more power to existing instances).</p>
      
      <h3>3. Observability</h3>
      <p>Observability is the ability to understand what's happening inside your system. This includes monitoring, logging, tracing, and alerting.</p>
      
      <h2>Common Failure Patterns</h2>
      <p>Understanding how systems fail is the first step to building resilience. Here are the most common failure patterns:</p>
      
      <h3>1. Cascading Failures</h3>
      <p>Cascading failures occur when the failure of one component causes other components to fail, creating a chain reaction. This often happens when systems are tightly coupled or when there's no proper isolation.</p>
      
      <h3>2. Thundering Herd</h3>
      <p>The thundering herd problem occurs when many clients try to access a resource simultaneously, overwhelming the system. This commonly happens during cache misses or when restarting services.</p>
      
      <h3>3. Resource Exhaustion</h3>
      <p>Resource exhaustion occurs when a system runs out of critical resources like memory, CPU, or network bandwidth. This can lead to performance degradation or complete system failure.</p>
      
      <h2>Resilience Patterns</h2>
      <p>Here are proven patterns for building resilient systems:</p>
      
      <h3>1. Circuit Breaker</h3>
      <p>The circuit breaker pattern prevents cascading failures by temporarily stopping calls to a failing service. When the service recovers, the circuit breaker allows calls to resume.</p>
      
      <h3>2. Bulkhead</h3>
      <p>The bulkhead pattern isolates critical resources to prevent failures from spreading. This is like the watertight compartments in a ship that prevent the entire vessel from sinking.</p>
      
      <h3>3. Retry with Exponential Backoff</h3>
      <p>Retrying failed operations with exponential backoff helps handle transient failures while avoiding overwhelming the system with too many retry attempts.</p>
      
      <h3>4. Timeout and Deadlines</h3>
      <p>Setting appropriate timeouts and deadlines prevents operations from hanging indefinitely and consuming resources.</p>
      
      <h2>Monitoring and Alerting</h2>
      <p>You can't improve what you can't measure. Here's how to build effective monitoring and alerting:</p>
      
      <h3>Key Metrics to Monitor</h3>
      <ul>
        <li><strong>Availability:</strong> Percentage of time the system is operational</li>
        <li><strong>Latency:</strong> Time taken to process requests</li>
        <li><strong>Throughput:</strong> Number of requests processed per second</li>
        <li><strong>Error Rate:</strong> Percentage of requests that result in errors</li>
        <li><strong>Resource Utilization:</strong> CPU, memory, and network usage</li>
      </ul>
      
      <h3>Alerting Best Practices</h3>
      <ul>
        <li>Set up alerts for critical metrics that indicate system health</li>
        <li>Use different severity levels for different types of issues</li>
        <li>Include context and actionable information in alerts</li>
        <li>Test your alerting system regularly</li>
        <li>Avoid alert fatigue by tuning thresholds appropriately</li>
      </ul>
      
      <h2>Testing for Resilience</h2>
      <p>Testing is crucial for building resilient systems. Here are different types of tests you should implement:</p>
      
      <h3>1. Load Testing</h3>
      <p>Load testing helps you understand how your system behaves under expected load conditions.</p>
      
      <h3>2. Stress Testing</h3>
      <p>Stress testing pushes your system beyond its normal capacity to find breaking points.</p>
      
      <h3>3. Chaos Engineering</h3>
      <p>Chaos engineering involves intentionally introducing failures to test system resilience in production.</p>
      
      <h2>Case Study: Netflix's Chaos Monkey</h2>
      <p>Netflix's Chaos Monkey is a famous example of chaos engineering. It randomly terminates instances in production to ensure that the system can handle failures gracefully.</p>
      
      <p>Key lessons from Netflix's approach:</p>
      <ul>
        <li>Start small and gradually increase the scope of chaos</li>
        <li>Have a clear rollback plan</li>
        <li>Monitor the impact of chaos experiments</li>
        <li>Learn from failures and improve the system</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>Building resilient systems is an ongoing process that requires careful planning, monitoring, and continuous improvement. By understanding failure patterns and implementing resilience patterns, you can create systems that survive and thrive under pressure.</p>
      
      <p>Remember: resilience is not about preventing all failures – it's about failing gracefully and recovering quickly.</p>
    `,
    author: 'David Kim',
    authorId: 'david-kim',
    newsletter: 'Engineering Weekly',
    newsletterId: 'engineering-weekly',
    publishedAt: new Date(Date.now() - 12 * 60 * 60 * 1000),
    readTime: 14,
    tags: ['system design', 'resilience', 'microservices', 'monitoring', 'chaos engineering'],
    imageUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop',
    likes: 94,
    isLiked: true,
    isBookmarked: true,
    isStarred: false,
    isFlagged: false,
    priority: 'normal',
    category: 'Engineering',
    excerpt: 'Learn how to build systems that can survive and thrive under pressure, with lessons from high-scale applications.',
    views: 2134,
    shares: 67,
    comments: 19
  }
];

export const testNewsletters: TestNewsletter[] = [
  {
    id: 'tech-weekly',
    title: 'Tech Weekly',
    author: 'Sarah Chen',
    description: 'Weekly insights on technology, web development, and the future of software engineering.',
    category: 'Technology',
    subscriberCount: 125000,
    avgRating: 4.8,
    postCount: 156,
    frequency: 'Weekly',
    lastPost: new Date(Date.now() - 2 * 60 * 60 * 1000),
    tags: ['technology', 'web development', 'programming', 'ai', 'future'],
    color: 'blue',
    isRecommended: true,
    isTrending: true,
    isNew: false,
    isSubscribed: false,
    engagementScore: 92,
    qualityScore: 95,
    readingTime: 8,
    language: 'English',
    price: 'free'
  },
  {
    id: 'react-digest',
    title: 'React Digest',
    author: 'Mike Johnson',
    description: 'The latest React news, tutorials, and best practices for modern developers.',
    category: 'Technology',
    subscriberCount: 89000,
    avgRating: 4.6,
    postCount: 203,
    frequency: 'Weekly',
    lastPost: new Date(Date.now() - 1 * 60 * 60 * 1000),
    tags: ['react', 'javascript', 'frontend', 'tutorials', 'best practices'],
    color: 'purple',
    isRecommended: true,
    isTrending: false,
    isNew: false,
    isSubscribed: true,
    engagementScore: 88,
    qualityScore: 92,
    readingTime: 6,
    language: 'English',
    price: 'free'
  },
  {
    id: 'design-weekly',
    title: 'Design Weekly',
    author: 'Emma Wilson',
    description: 'The latest in design systems, UI/UX trends, and design tools for modern teams.',
    category: 'Design',
    subscriberCount: 67000,
    avgRating: 4.4,
    postCount: 89,
    frequency: 'Bi-weekly',
    lastPost: new Date(Date.now() - 3 * 60 * 60 * 1000),
    tags: ['design', 'ui/ux', 'design systems', 'tools', 'trends'],
    color: 'green',
    isRecommended: false,
    isTrending: true,
    isNew: false,
    isSubscribed: false,
    engagementScore: 85,
    qualityScore: 88,
    readingTime: 10,
    language: 'English',
    price: 'paid',
    priceAmount: 5
  },
  {
    id: 'product-insights',
    title: 'Product Insights',
    author: 'Alex Rodriguez',
    description: 'Weekly analysis of product management, user experience, and growth strategies.',
    category: 'Product',
    subscriberCount: 45000,
    avgRating: 4.7,
    postCount: 124,
    frequency: 'Weekly',
    lastPost: new Date(Date.now() - 5 * 60 * 60 * 1000),
    tags: ['product management', 'ux', 'growth', 'strategy', 'analytics'],
    color: 'orange',
    isRecommended: true,
    isTrending: false,
    isNew: false,
    isSubscribed: true,
    engagementScore: 90,
    qualityScore: 93,
    readingTime: 7,
    language: 'English',
    price: 'free'
  },
  {
    id: 'engineering-weekly',
    title: 'Engineering Weekly',
    author: 'David Kim',
    description: 'Deep dives into system design, architecture patterns, and engineering best practices.',
    category: 'Engineering',
    subscriberCount: 78000,
    avgRating: 4.5,
    postCount: 98,
    frequency: 'Weekly',
    lastPost: new Date(Date.now() - 4 * 60 * 60 * 1000),
    tags: ['engineering', 'system design', 'architecture', 'scalability', 'performance'],
    color: 'indigo',
    isRecommended: false,
    isTrending: true,
    isNew: false,
    isSubscribed: false,
    engagementScore: 87,
    qualityScore: 91,
    readingTime: 12,
    language: 'English',
    price: 'free'
  },
  {
    id: 'startup-insights',
    title: 'Startup Insights',
    author: 'Lisa Park',
    description: 'Weekly analysis of startup trends, funding rounds, and entrepreneurial insights.',
    category: 'Business',
    subscriberCount: 56000,
    avgRating: 4.3,
    postCount: 67,
    frequency: 'Bi-weekly',
    lastPost: new Date(Date.now() - 6 * 60 * 60 * 1000),
    tags: ['startups', 'business', 'entrepreneurship', 'funding', 'growth'],
    color: 'red',
    isRecommended: true,
    isTrending: false,
    isNew: true,
    isSubscribed: false,
    engagementScore: 83,
    qualityScore: 86,
    readingTime: 9,
    language: 'English',
    price: 'paid',
    priceAmount: 3
  }
];

export const testCategories = [
  { id: 'technology', name: 'Technology', count: 245, color: 'blue' },
  { id: 'design', name: 'Design', count: 189, color: 'purple' },
  { id: 'business', name: 'Business', count: 156, color: 'green' },
  { id: 'engineering', name: 'Engineering', count: 98, color: 'indigo' },
  { id: 'product', name: 'Product', count: 134, color: 'orange' },
  { id: 'science', name: 'Science', count: 76, color: 'pink' },
  { id: 'lifestyle', name: 'Lifestyle', count: 203, color: 'yellow' },
  { id: 'news', name: 'News', count: 167, color: 'red' }
];

export const testReadingStats = {
  totalArticlesRead: 156,
  totalReadingTime: 1240, // minutes
  averageReadingTime: 8,
  readingStreak: 12,
  favoriteCategories: ['Technology', 'Design', 'Business'],
  readingGoal: 30,
  goalProgress: 22,
  weeklyReadingTime: 180,
  monthlyReadingTime: 720,
  favoriteAuthors: ['Sarah Chen', 'Emma Wilson', 'Mike Johnson'],
  readingTrends: [
    { week: 'Week 1', articles: 8, time: 120 },
    { week: 'Week 2', articles: 12, time: 180 },
    { week: 'Week 3', articles: 15, time: 225 },
    { week: 'Week 4', articles: 18, time: 270 }
  ]
};

export const testQueueItems = [
  {
    id: '1',
    title: 'The Future of Web Development',
    author: 'Sarah Chen',
    newsletter: 'Tech Weekly',
    readTime: 8,
    priority: 'urgent' as const,
    addedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
    isRead: false,
    isStarred: true,
    isArchived: false,
    tags: ['web', 'development', 'future'],
    excerpt: 'Exploring the latest trends and technologies shaping the future of web development...',
    category: 'Technology',
    imageUrl: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=200&fit=crop'
  },
  {
    id: '2',
    title: 'Understanding React Server Components',
    author: 'Mike Johnson',
    newsletter: 'React Digest',
    readTime: 12,
    priority: 'normal' as const,
    addedAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
    isRead: false,
    isStarred: false,
    isArchived: false,
    tags: ['react', 'server-components', 'performance'],
    excerpt: 'A deep dive into React Server Components and how they can improve your application performance...',
    category: 'Technology',
    imageUrl: 'https://images.unsplash.com/photo-1633356122544-f134324a6cce?w=400&h=200&fit=crop'
  },
  {
    id: '3',
    title: 'Design Systems That Scale',
    author: 'Emma Wilson',
    newsletter: 'Design Weekly',
    readTime: 6,
    priority: 'low' as const,
    addedAt: new Date(Date.now() - 6 * 60 * 60 * 1000),
    isRead: true,
    readAt: new Date(Date.now() - 1 * 60 * 60 * 1000),
    isStarred: false,
    isArchived: false,
    tags: ['design', 'systems', 'scalability'],
    excerpt: 'How to build design systems that grow with your product and team...',
    category: 'Design',
    imageUrl: 'https://images.unsplash.com/photo-1558655146-d09347e92766?w=400&h=200&fit=crop'
  }
];

// Helper function to get random test data
export const getRandomArticle = (): TestArticle => {
  return testArticles[Math.floor(Math.random() * testArticles.length)];
};

export const getRandomNewsletter = (): TestNewsletter => {
  return testNewsletters[Math.floor(Math.random() * testNewsletters.length)];
};

export const getRandomQueueItem = () => {
  return testQueueItems[Math.floor(Math.random() * testQueueItems.length)];
};
